/**
 * Canonical pricingOption value→label map — the single read-side source of
 * truth for how a listing's `publicData.pricingOption` renders to buyers.
 *
 * Story 12.8: five read-side components used to each declare their own drifted
 * copy of this map (ListingCard, OrderPanel, SectionPricingCard, Lightbox,
 * SectionRelatedHorses), which let labels diverge — e.g. "Contact for Price"
 * vs "Contact for price", "Auction" vs "Consigned to Auction", or `contact`
 * missing entirely so it silently fell through to a default literal. They now
 * all import from here, so the four wizard-exposed values render identically
 * everywhere a buyer encounters them.
 *
 * Covers ALL nine data-model values (not just the four the create-listing
 * wizard exposes) so migrated/legacy listings keep rendering:
 *   - asking_price / list_price → NO label; the reader renders formatted money.
 *   - contact / private_treaty / auction → buyer-facing no-price labels.
 *   - lease / trade / stud_fee → not exposed in the wizard picker, but present
 *     on migrated listings; keep their labels so those listings still render.
 *   - contact_seller → in the hosted-config enum only; labelled like `contact`.
 * A label is a DISPLAY rule. Whether an option carries a price at all is
 * `isNoPricePricingOption` below, and the two sets differ.
 *
 * These are plain English constants (matching the existing readers — this is a
 * display map, not i18n).
 */
export const PRICING_OPTION_LABELS = {
  // Price-bearing types: no label — the reader formats the Money value instead.
  asking_price: null,
  list_price: null,
  // No-price types — canonical buyer-facing casing (resolves the prior drift).
  contact: 'Contact for Price',
  private_treaty: 'Private treaty',
  auction: 'Consigned to auction',
  // Not exposed in the wizard picker, but present on migrated/legacy listings.
  lease: 'Lease',
  trade: 'Trade',
  stud_fee: 'Stud fee',
  // Story 12.30 (R1) — a value of the hosted-config enum, not of the wizard
  // picker. The same label as `contact`, which is what the ad renderer already
  // draws for it (tph-api `adTemplateRenderer/derive.js` `priceTagSpec`).
  contact_seller: 'Contact for Price',
};

/**
 * 🔴 Story 12.30 (C3.4, operator ruling R1 2026-10-09) — the options that carry
 * NO price. A save on one of these clears `price`, and the listing page's
 * JSON-LD names no price for it.
 *
 * This is deliberately NOT "has a label". `lease`, `trade` and `stud_fee` have
 * labels, but their number can be a real fee the seller entered, so they keep
 * their price on save and in JSON-LD, and the tph-api cleanup never targets
 * them. tph-api `scripts/adhoc/clear-hidden-price-on-no-price-listings.js`
 * carries the same four values; change both together.
 */
export const NO_PRICE_PRICING_OPTIONS = ['contact', 'private_treaty', 'auction', 'contact_seller'];

/**
 * @param {string} [pricingOption]
 * @returns {boolean} true when the option carries no price (NO_PRICE_PRICING_OPTIONS)
 */
export const isNoPricePricingOption = pricingOption =>
  NO_PRICE_PRICING_OPTIONS.includes(pricingOption);

/**
 * Resolve a pricingOption to its canonical label, or null when the type is
 * price-bearing (asking_price / list_price) or unknown — in which case the
 * caller should render the formatted price. This mirrors the truthy check each
 * reader already performed against its local map, so the money-vs-label
 * branching is preserved exactly.
 *
 * @param {string} [pricingOption]
 * @returns {string|null}
 */
export const getPricingOptionLabel = pricingOption => PRICING_OPTION_LABELS[pricingOption] || null;
